<template>
  <div class="scheduler-container scheduler-container--pricing">
    <spinner-loader v-if="loading" />

    <DayPilotScheduler
      id="pricing-scheduler"
      ref="scheduler"
      :config="config"
      :resources="resources"
    />

    <div class="scheduler-date-tools">
      <b-button @click="scrollToToday" class="scheduler-today-button">
        {{ $t("scheduler.today_button").toUpperCase() }}
      </b-button>
      <i class="scheduler-cal-icon fi-calendar" />
      <v-date-picker
        v-model="selectedDate"
        class="scheduler-date-picker"
        mode="single"
        show-caps
      />
    </div>

    <pricing-rules-modal ref="pricing-rules-modal" @updated="update" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { DayPilot, DayPilotScheduler } from "daypilot-pro-vue";
import PricingMixin from "@/mixins/pricing-mixin";

export default {
  name: "PricingScheduler",

  components: {
    DayPilotScheduler,
    PricingRulesModal: () => import("@/components/modals/PricingRulesModal"),
    SpinnerLoader: () => import("@/components/widgets/SpinnerLoader"),
  },

  mixins: [PricingMixin],

  data() {
    return {
      loading: false,
      selectedDate: new Date(Date.now()),
      schedulerLocales: {
        en: "en-us",
        es: "es-es",
        xx: "es-es",
      },
      schedulerDayShortNames: {
        en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
        es: ["Dom.", "Lun.", "Mar.", "Mié.", "Jue.", "Vie.", "Sáb."],
        xx: ["Dom.", "Lun.", "Mar.", "Mié.", "Jue.", "Vie.", "Sáb."],
      },
      config: {
        timeHeaders: [
          {
            // TODO: add class with DayPilot.Scheduler.onBeforeTimeHeaderRender(args)
            groupBy: "Month",
          },
          {
            groupBy: "Day",
            format: "ddd",
          },
          {
            groupBy: "Day",
            format: "d",
          },
        ],
        scale: "Day",
        scrollTo: DayPilot.Date.today(),
        // floatingTimeHeaders: true,
        theme: "scheduler_white",
        cellGroupBy: "Month",
        days: 90,
        cellDuration: 1440,
        moveBy: "Full",
        allowMultiRange: false,
        // multiSelectRectangle: 'Free',
        dynamicLoading: false,
        infiniteScrollingEnabled: true,
        allowEventOverlap: false,
        dynamicEventRendering: "Enabled",
        crosshairType: "full",
        treeEnabled: true,
        rowHeaderWidth: window.innerWidth < 768 ? 150 : 200,
        rowHeaderWidthAutoFit: false,
        eventHeight: 54,
        cellWidth: 54,
        treePreventParentUsage: true,
        headerHeight: 18,
        headerDateFormat: "dddd",
        heightSpec: "Parent100Pct",
        rowHeaderWidthAutoFit: false,
        progressiveRowRendering: true,
        eventHoverHandling: "Bubble",
        eventResizeHandling: "disabled",
        eventMovingStartEndEnabled: true,
        eventMoveHandling: "disabled",
        // scrollDelayDynamic: 10,
        // Interaction Callbacks
        onTimeRangeSelecting: this.onTimeRangeSelecting,
        onTimeRangeSelected: this.onTimeRangeSelected,
        onEventClicked: this.onEventClicked,
        onResourceHeaderClick: this.onResourceHeaderClick,
        onRowHeaderClick: this.onRowHeaderClick,
        // Rendering Callbacks
        onBeforeRowHeaderRender: this.onBeforeRowHeaderRender,
        onBeforeCellRender: this.onBeforeCellRender,
      },
    };
  },

  computed: {
    ...mapGetters([
      "getUnits",
      "getUnitPricingRules",
      "getUnitTypesList",
      "getReservations",
    ]),

    scheduler() {
      return this.$refs.scheduler.control;
    },

    events() {
      return this.getReservations
        .map((reservation) => {
          let unit_rate = 0;

          if (reservation.rate) {
            if (reservation.rate_type_id === 2) {
              let starts_at = this.$moment(reservation.starts_at);
              let ends_at = this.$moment(reservation.ends_at);

              let diff = ends_at.diff(starts_at, "day");
              unit_rate =
                diff > 0 ? parseFloat((reservation.rate / diff).toFixed(2)) : 0;
            } else {
              unit_rate = reservation.rate;
            }
          }

          return reservation.pages.map((page) => ({
            id: page.id,
            rate: unit_rate || page.rate,
            start: this.$moment(page.date).format("YYYY-MM-DD"),
            end: this.$moment(page.date).format("YYYY-MM-DD"),
            resource: reservation.unit_id,
            unit_price_id: page.unit_price_id,
          }));
        })
        .flat();
    },

    resources() {
      let resources_ = [];

      this.getUnitTypesList.map((unit_type) => {
        unit_type.unit.map((unit_) => {
          resources_.push({
            id: unit_.id,
            name: unit_.unit_name,
            tags: {
              unit: unit_,
              unit_type: unit_type,
            },
          });
        });
      });

      return resources_;
    },
  },

  mounted() {
    const locale = localStorage.getItem("locale") || "en";

    if (this.scheduler && this.schedulerLocales[locale]) {
      this.scheduler.locale = this.schedulerLocales[locale];

      // Only set dayNamesShort if the locale exists in DayPilot
      if (DayPilot.Locale.all[this.scheduler.locale]) {
        DayPilot.Locale.all[this.scheduler.locale].dayNamesShort =
          this.schedulerDayShortNames[locale];
      }
    }

    // Add resize event listener
    window.addEventListener("resize", this.handleResize);

    this.update();
  },

  beforeDestroy() {
    // Remove resize event listener
    window.removeEventListener("resize", this.handleResize);
  },

  methods: {
    handleResize() {
      // Update rowHeaderWidth based on window width
      this.config.rowHeaderWidth = window.innerWidth < 768 ? 150 : 200;
      this.update();
    },

    getUnitRateOfDate(unit_id, date) {
      let event = this.events.find(
        (event) => event.resource === unit_id && event.start === date
      );

      if (event) {
        return {
          disabled: true,
          type: "event",
          value: event.rate,
          rule_id: event.unit_price_id,
        };
      }

      let rules = this.getPricingRules(unit_id, date);

      if (rules.length) {
        return {
          disabled: true,
          type: "manual",
          value: rules[0].rate,
          backColor: rules[0].color || this.defaultRuleColor,
        };
      }

      let resource = this.resources.find((resource) => resource.id === unit_id);

      if (resource) {
        return {
          disabled: false,
          type: "default",
          value: resource.tags.unit.unit_rate,
        };
      }

      return {
        default: true,
        type: "unknown",
        value: 0,
      };
    },

    onTimeRangeSelecting(args) {
      let cells = args.row.cells.forRange(args.start, args.end);

      if (cells.length >= 2) {
        args.allowed = false;
      } else {
        args.allowed = !cells.find((cell) => !!cell.properties.is_event);
      }
    },

    onTimeRangeSelected(args) {
      let date = args.start.value.split("T")[0];
      let rules = this.getPricingRules(args.resource, date);

      if (rules.length) {
        this.showPricingRulesModal(args.resource, date, false);
      }
    },

    onResourceHeaderClick(args) {
      this.showPricingRulesModal(args.resource.id);
    },

    onRowHeaderClick(args) {
      if (args.action === "edit") {
        const unitId = args.source.getAttribute("data-unit-id");
        if (unitId) {
          this.showPricingRulesModal(unitId);
        }
      }
    },

    onEventClicked(args) {
      console.log("onEventClicked", args);
    },

    onBeforeCellRender(args) {
      let date = args.cell.start.value.split("T")[0];
      let rate = this.getUnitRateOfDate(args.cell.resource, date);

      rate.value = parseFloat(rate.value);

      if (rate.type === "default") {
        args.cell.html = `<div class="reservation-event">${rate.value}</div>`;
        args.cell.properties.is_event = false;
      } else if (rate.type === "manual") {
        args.cell.properties.is_event = false;
        args.cell.properties.is_rule = true;
        args.cell.html = `<div class="reservation-event" style="background-color: ${rate.backColor}">${rate.value}</div>`;
      } else if (rate.type === "event") {
        args.cell.properties.is_event = true;
        args.cell.properties.rule_id = rate.rule_id;
        args.cell.html = `<div class="reservation-event reservation-event--event">${rate.value}</div>`;
      }
    },

    onBeforeRowHeaderRender(args) {
      let unit_type = args.row.tags.unit_type.unit_type_name;
      let unit_name = args.row.tags.unit.unit_name;
      let unit_id = args.row.tags.unit.id;

      let html =
        '<div style="margin-left: 5px;" class="scheduler_white_rowheader_inner_indent">';
      html +=
        '<div style="width: 10px; height: 10px; background-repeat: no-repeat; position: absolute; top: 3px;" class="scheduler_white_tree_image_no_children"></div>';
      html +=
        '<div style="margin-left: 12px;" class="scheduler_white_rowheader_inner_text">';
      html += '<div><div class="row-header-unit-label">' + unit_name + "</div>";
      html +=
        '<div class="row-header-unit-type-label">' +
        unit_type.toUpperCase() +
        "</div></div>";
      html += "</div>";
      html +=
        '<div class="row-header-edit-icon" data-unit-id="' +
        unit_id +
        '" onclick="DayPilot.Scheduler.rowHeaderClick(this, \'edit\')"><i class="fi-pencil" title="Edit unit"></i></div>';
      html += "</div>";

      args.row.html = html;
    },

    scrollToDate(_dateString) {
      this.scheduler.scrollTo(new DayPilot.Date(_dateString));
    },

    scrollToToday() {
      this.scheduler.scrollTo(DayPilot.Date.today());
    },

    showPricingRulesModal(unit_id, date, editable = true) {
      let rules_modal = this.$refs["pricing-rules-modal"];

      if (rules_modal) {
        rules_modal.show(unit_id, date, editable);
      }
    },

    update() {
      this.scheduler.update({
        resources: this.resources,
      });
    },
  },

  watch: {
    selectedDate() {
      this.scrollToDate(this.selectedDate);
    },

    resources() {
      this.update();
    },

    events() {
      this.update();
    },

    getUnitPricingRules() {
      console.log("getUnitPricingRules updated");
      this.update();
    },
  },
};
</script>

<style lang="scss">
@import "@/style/_scheduler.scss";

.row-header-edit-icon {
  position: absolute;
  right: 10px;
  top: 15px;
  color: white;
  cursor: pointer;
}

.row-header-edit-icon i {
  font-size: 16px;
}

.row-header-edit-icon:hover {
  color: #f7f7f7;
}

.scheduler-date-tools {
  @media (max-width: 767px) {
    width: 150px !important;

    .scheduler-cal-icon {
      margin-left: 3px;
    }
  }
}

.scheduler-today-button {
  @media (max-width: 767px) {
    padding: 0.2rem 0.4rem !important;
    font-size: 0.7rem !important;
    line-height: 1 !important;
    margin-right: 2px !important;
    max-width: 60px !important;
    min-width: auto !important;
  }
}
</style>
